import React from "react";
import styles from "../components/Dashboard.module.css";
import ConfirmationModal from "../components/ConfirmationModal";
import AgentDatasetCoverage from "../components/AgentDatasetCoverage";
import {
  agentsApi,
  Agent,
  AgentFormValues,
  emptyAgentForm,
  agentToForm,
  resolveConnectorType,
  ValidationState,
  parseHeadersJson,
  DEFAULT_OPENAI_COMPLETIONS_ENDPOINT,
  DEFAULT_OPENAI_RESPONSES_ENDPOINT,
  isManagedModelAgent,
  managedModelModeLabel,
  managedModelProviderLabel,
} from "../services/agentsApi";

const toLocalDateTime = (iso: string) => new Date(iso).toLocaleString();

const AGENT_ID_PATTERN = /^[a-z0-9][a-z0-9_-]*$/i;

function sortAgentsForList(agents: Agent[]): Agent[] {
  return [...agents].sort((a, b) => {
    if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;
    return b.createdAt.localeCompare(a.createdAt);
  });
}

const AgentsPage: React.FC = () => {
  const [agents, setAgents] = React.useState<Agent[]>([]);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [isCreating, setIsCreating] = React.useState(false);
  const [form, setForm] = React.useState<AgentFormValues>(() => emptyAgentForm());
  const [errors, setErrors] = React.useState<string[]>([]);
  const [feedback, setFeedback] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const rows = sortAgentsForList(await agentsApi.listAgents());
        if (!mounted) return;
        setAgents(rows);
        setSelectedId(null);
      } catch (e: unknown) {
        if (!mounted) return;
        setLoadError(e instanceof Error ? e.message : "Failed to load agents.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  const selectedAgent = agents.find((a) => a.id === selectedId) || null;
  const baselineEditForm = React.useMemo(
    () => (selectedAgent && !isCreating ? agentToForm(selectedAgent) : null),
    [selectedAgent, isCreating],
  );
  const hasFormChanges = React.useMemo(() => {
    if (isCreating) return true;
    if (!baselineEditForm) return false;
    if (selectedAgent && isManagedModelAgent(selectedAgent)) {
      return form.name.trim() !== baselineEditForm.name.trim();
    }
    return JSON.stringify(form) !== JSON.stringify(baselineEditForm);
  }, [isCreating, baselineEditForm, form, selectedAgent]);

  React.useEffect(() => {
    if (selectedAgent && !isCreating) {
      setForm(agentToForm(selectedAgent));
    } else if (isCreating) {
      setForm(emptyAgentForm());
    }
  }, [selectedAgent, isCreating]);

  const validationStateLabel = (state: ValidationState) => {
    if (state === "not_validated") return "Not validated";
    if (state === "valid") return "Valid";
    if (state === "warning") return "Warning";
    return "Error";
  };

  const agentCardToneClass = (state: ValidationState) => {
    if (state === "valid") return styles.agentItemToneValid;
    if (state === "warning") return styles.agentItemToneWarn;
    if (state === "error") return styles.agentItemToneError;
    return styles.agentItemToneNeutral;
  };

  const validateLocal = (): string[] => {
    const nextErrors: string[] = [];
    if (!form.name.trim()) nextErrors.push("Agent name is required.");

    if (isCreating && form.agentId.trim()) {
      if (!AGENT_ID_PATTERN.test(form.agentId.trim())) {
        nextErrors.push(
          "Agent ID must start with a letter or number and contain only letters, numbers, underscores, or hyphens.",
        );
      }
    }

    const ct = resolveConnectorType(form);

    if (ct === "openai_responses" || ct === "openai_completions") {
      if (!form.model.trim()) nextErrors.push("Model is required.");
      if (!form.endpoint.trim()) nextErrors.push("Service URL is required.");
      else {
        try {
          // eslint-disable-next-line no-new
          new URL(form.endpoint.trim());
        } catch {
          nextErrors.push("Service URL must be valid.");
        }
      }
      if (!form.apiKeyRef.trim()) nextErrors.push("API key reference is required.");
      const t = Number(form.temperature);
      const maxTokens = Number(form.maxOutputTokens);
      if (Number.isNaN(t) || t < 0 || t > 2) {
        nextErrors.push("Temperature must be a number between 0 and 2.");
      }
      if (!Number.isInteger(maxTokens) || maxTokens <= 0) {
        nextErrors.push("Max output tokens must be a positive integer.");
      }
    }

    if (ct === "mcp_sse" || ct === "mcp_http") {
      if (!form.url.trim()) nextErrors.push("Server URL is required for MCP SSE/HTTP.");
      else {
        try {
          // eslint-disable-next-line no-new
          new URL(form.url.trim());
        } catch {
          nextErrors.push("Server URL must be a valid URL.");
        }
      }
      if (!form.defaultTool.trim()) nextErrors.push("Default tool is required for MCP SSE/HTTP.");
      try {
        parseHeadersJson(form.headersJson);
      } catch (e) {
        nextErrors.push(e instanceof Error ? e.message : "Invalid headers JSON.");
      }
    }

    const timeout = Number(form.requestTimeoutMs);
    if (!Number.isFinite(timeout) || timeout <= 0) {
      nextErrors.push("Request timeout must be a positive number (milliseconds).");
    }

    return nextErrors;
  };

  const handleCreate = () => {
    setIsCreating(true);
    setSelectedId(null);
    setErrors([]);
    setFeedback(null);
  };

  const handleSave = async () => {
    const nextErrors = validateLocal();
    setErrors(nextErrors);
    setFeedback(null);
    if (nextErrors.length > 0) return;

    setBusy(true);
    try {
      if (isCreating) {
        const created = await agentsApi.createAgent(form);
        setAgents((prev) => sortAgentsForList([created, ...prev]));
        setSelectedId(created.id);
        setIsCreating(false);
        setFeedback("Agent created.");
        return;
      }

      if (!selectedAgent) return;
      const updated =
        isManagedModelAgent(selectedAgent)
          ? await agentsApi.updateAgentName(selectedAgent.id, form.name)
          : await agentsApi.updateAgent(selectedAgent.id, form);
      setAgents((prev) => sortAgentsForList(prev.map((agent) => (agent.id === updated.id ? updated : agent))));
      setFeedback("Agent updated.");
    } catch (e: unknown) {
      setErrors([e instanceof Error ? e.message : "Failed to save agent."]);
    } finally {
      setBusy(false);
    }
  };

  const toggleEnabled = async (agentId: string) => {
    setBusy(true);
    setFeedback(null);
    try {
      const current = agents.find((a) => a.id === agentId);
      const updated = await agentsApi.setAgentEnabled(agentId, !current?.enabled);
      setAgents((prev) => sortAgentsForList(prev.map((agent) => (agent.id === updated.id ? updated : agent))));
      setFeedback("Agent status updated.");
    } catch (e: unknown) {
      setErrors([e instanceof Error ? e.message : "Failed to change agent status."]);
    } finally {
      setBusy(false);
    }
  };

  const confirmDeleteAgent = async () => {
    if (!selectedAgent) return;
    setBusy(true);
    try {
      await agentsApi.deleteAgent(selectedAgent.id);
      const next = agents.filter((agent) => agent.id !== selectedAgent.id);
      setAgents(next);
      setSelectedId(null);
      setIsCreating(false);
      setFeedback("Agent deleted.");
      setErrors([]);
      setDeleteModalOpen(false);
    } catch (e: unknown) {
      setErrors([e instanceof Error ? e.message : "Failed to delete agent."]);
    } finally {
      setBusy(false);
    }
  };

  const validateSelected = async () => {
    if (!selectedAgent) return;
    setBusy(true);
    setFeedback(null);
    setErrors([]);
    try {
      const updated = await agentsApi.validateAgent(selectedAgent.id);
      setAgents((prev) => sortAgentsForList(prev.map((agent) => (agent.id === updated.id ? updated : agent))));
      setFeedback(`Validation completed: ${validationStateLabel(updated.validationState)}.`);
    } catch (e: unknown) {
      setErrors([e instanceof Error ? e.message : "Failed to validate agent."]);
    } finally {
      setBusy(false);
    }
  };

  const handlePick = (agentId: string) => {
    setSelectedId(agentId);
    setIsCreating(false);
    setErrors([]);
    setFeedback(null);
  };

  const showOpenAiFields =
    !selectedAgent || !isManagedModelAgent(selectedAgent)
      ? form.connectionFamily === "openai_responses" || form.connectionFamily === "openai_completions"
      : false;
  const showMcpNetwork =
    !selectedAgent || !isManagedModelAgent(selectedAgent)
      ? form.connectionFamily === "mcp"
      : false;
  const showManagedModelDetails = Boolean(selectedAgent && !isCreating && isManagedModelAgent(selectedAgent));

  if (loading) {
    return (
      <div className={styles.dashboard}>
        <section className={styles.dashboardLoading} aria-live="polite" aria-busy="true">
          <div className={styles.dashboardLoadingSpinner} />
          <p className={styles.dashboardLoadingText}>Loading agents...</p>
        </section>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={styles.agentsPage}>
        <section className={styles.agentsListCard}>
          <div className={styles.metricsErrorBox}>
            <p>{loadError}</p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div
      className={`${styles.agentsPage} ${
        isCreating || selectedAgent ? styles.agentsPageSplit : styles.agentsPageListOnly
      }`.trim()}
    >
      <section className={styles.agentsListCard}>
        <div className={styles.agentsListHeader}>
          <div>
            <h1 className={styles.agentsTitle}>Agents</h1>
            <p className={styles.agentsSubtitle}>
              Register AI assistants, connect them to evaluation scenarios, and monitor quality over time.
            </p>
          </div>
          <button type="button" className={styles.agentsPrimaryBtn} onClick={handleCreate}>
            + New agent
          </button>
        </div>

        <div className={styles.agentsList}>
          {agents.map((agent) => (
            <button
              key={agent.id}
              type="button"
              className={`${styles.agentItem} ${agentCardToneClass(agent.validationState)} ${
                selectedId === agent.id && !isCreating ? styles.agentItemActive : ""
              }`.trim()}
              onClick={() => handlePick(agent.id)}
            >
              <div className={styles.agentItemTop}>
                <strong className={styles.agentItemName}>{agent.name}</strong>
                <span
                  className={`${styles.agentBadge} ${
                    agent.enabled ? styles.agentBadgeEnabled : styles.agentBadgeDisabled
                  }`.trim()}
                >
                  {agent.enabled ? "Enabled" : "Disabled"}
                </span>
              </div>
              {agent.summaryLines.map((line, idx) => (
                <p key={`${agent.id}-line-${idx}`} className={styles.agentItemMeta}>
                  {line}
                </p>
              ))}
              <p className={styles.agentItemMeta}>
                Validation: {validationStateLabel(agent.validationState)}
              </p>
            </button>
          ))}
          {agents.length === 0 ? (
            <p className={styles.agentsSubtitle}>No agents configured yet.</p>
          ) : null}
        </div>
      </section>

      {isCreating || selectedAgent ? (
        <section className={`${styles.agentsEditorCard} ${styles.agentsEditorCardAnimated}`.trim()}>
          <h2 className={styles.agentsEditorTitle}>{isCreating ? "Create agent" : "Edit agent"}</h2>
          <p className={styles.agentsEditorHint}>
            Pick how this agent connects first, then name it and complete the connector fields. Validate after saving
            to refresh connection status.
          </p>

          {selectedAgent && !isCreating ? (
            <p className={styles.agentsAgentRefLine}>
              Reference ID: <code>{selectedAgent.id}</code>
            </p>
          ) : null}

          {selectedAgent && !isCreating ? (
            <div className={styles.agentValidationBox}>
              <strong>Latest validation: {validationStateLabel(selectedAgent.validationState)}</strong>
              <p>{selectedAgent.validationMessage}</p>
              {selectedAgent.validationState !== "not_validated" ? (
                <p>Checked: {toLocalDateTime(selectedAgent.validatedAt)}</p>
              ) : (
                <p>Validation pending.</p>
              )}
            </div>
          ) : null}

          {selectedAgent?.connector_type === "mcp_stdio" && !isCreating ? (
            <div className={styles.agentsMigrationNote}>
              This agent was saved as MCP stdio, which is no longer configurable here. Choose SSE or HTTP and enter
              server details before saving.
            </div>
          ) : null}

          {showManagedModelDetails && selectedAgent ? (
            <div className={styles.agentsManagedModelBox}>
              <p className={styles.agentsManagedModelIntro}>
                This assistant is connected through a managed model endpoint (provisioned during platform setup).
                Connection settings are read-only here — use Validate to confirm the model is ready for evaluation.
              </p>
              <dl className={styles.agentsManagedModelGrid}>
                <div>
                  <dt>Provider</dt>
                  <dd>{managedModelProviderLabel(selectedAgent.config)}</dd>
                </div>
                <div>
                  <dt>Model</dt>
                  <dd>{String(selectedAgent.config.model ?? "—")}</dd>
                </div>
                <div>
                  <dt>Service URL</dt>
                  <dd>{String(selectedAgent.config.base_url ?? "https://api.openai.com/v1")}</dd>
                </div>
                <div>
                  <dt>Evaluation mode</dt>
                  <dd>{managedModelModeLabel(selectedAgent.config.mode)}</dd>
                </div>
                <div>
                  <dt>Credentials</dt>
                  <dd>{String(selectedAgent.config.api_key_ref ?? "—")}</dd>
                </div>
                <div>
                  <dt>Request timeout</dt>
                  <dd>
                    {typeof selectedAgent.config.request_timeout_ms === "number"
                      ? `${selectedAgent.config.request_timeout_ms} ms`
                      : "—"}
                  </dd>
                </div>
              </dl>
            </div>
          ) : null}

          <div className={styles.agentsFormGrid}>
            {!showManagedModelDetails ? (
              <label className={styles.agentsField}>
                <span>Connection type</span>
                <select
                  className={styles.metricsInput}
                  value={form.connectionFamily}
                  onChange={(e) => {
                    const v = e.target.value as AgentFormValues["connectionFamily"];
                    setForm((prev) => {
                      let endpoint = prev.endpoint;
                      if (v === "openai_responses") endpoint = DEFAULT_OPENAI_RESPONSES_ENDPOINT;
                      if (v === "openai_completions") endpoint = DEFAULT_OPENAI_COMPLETIONS_ENDPOINT;
                      return {
                        ...prev,
                        connectionFamily: v,
                        endpoint,
                        mcpTransport: v === "mcp" ? "sse" : prev.mcpTransport,
                      };
                    });
                  }}
                >
                  <option value="openai_responses">OpenAI Responses API</option>
                  <option value="openai_completions">OpenAI Completions API</option>
                  <option value="mcp">MCP</option>
                </select>
              </label>
            ) : null}

            <label className={styles.agentsField}>
              <span>Name</span>
              <input
                className={styles.metricsInput}
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              />
            </label>

            {form.connectionFamily === "mcp" ? (
              <label className={styles.agentsField}>
                <span>MCP transport</span>
                <select
                  className={styles.metricsInput}
                  value={form.mcpTransport}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, mcpTransport: e.target.value as AgentFormValues["mcpTransport"] }))
                  }
                >
                  <option value="sse">SSE</option>
                  <option value="http">HTTP (streamable)</option>
                </select>
              </label>
            ) : null}

            {form.connectionFamily === "mcp" ? (
              <label className={styles.agentsField}>
                <span>Request timeout (ms)</span>
                <input
                  className={styles.metricsInput}
                  value={form.requestTimeoutMs}
                  onChange={(e) => setForm((prev) => ({ ...prev, requestTimeoutMs: e.target.value }))}
                />
              </label>
            ) : null}

            {isCreating ? (
              <label className={`${styles.agentsField} ${styles.agentsFieldSpan2}`}>
                <span>Optional reference ID</span>
                <span className={styles.agentsInputHint}>Leave blank to generate one from the name.</span>
                <input
                  className={styles.metricsInput}
                  placeholder="e.g. support-bot"
                  value={form.agentId}
                  onChange={(e) => setForm((prev) => ({ ...prev, agentId: e.target.value }))}
                />
              </label>
            ) : null}
          </div>

          {showOpenAiFields ? (
            <div className={styles.agentsFormGrid}>
              <label className={styles.agentsField}>
                <span>Model</span>
                <input
                  className={styles.metricsInput}
                  value={form.model}
                  onChange={(e) => setForm((prev) => ({ ...prev, model: e.target.value }))}
                />
              </label>
              <label className={styles.agentsField}>
                <span>Service URL</span>
                <input
                  className={styles.metricsInput}
                  value={form.endpoint}
                  onChange={(e) => setForm((prev) => ({ ...prev, endpoint: e.target.value }))}
                />
              </label>
              <label className={styles.agentsField}>
                <span>API key reference</span>
                <input
                  className={styles.metricsInput}
                  autoComplete="off"
                  placeholder="OPENAI_API_KEY_MAIN"
                  value={form.apiKeyRef}
                  onChange={(e) => setForm((prev) => ({ ...prev, apiKeyRef: e.target.value }))}
                />
              </label>
              <label className={styles.agentsField}>
                <span>Request timeout (ms)</span>
                <input
                  className={styles.metricsInput}
                  value={form.requestTimeoutMs}
                  onChange={(e) => setForm((prev) => ({ ...prev, requestTimeoutMs: e.target.value }))}
                />
              </label>
              <label className={styles.agentsField}>
                <span>Temperature (0–2)</span>
                <input
                  className={styles.metricsInput}
                  value={form.temperature}
                  onChange={(e) => setForm((prev) => ({ ...prev, temperature: e.target.value }))}
                />
              </label>
              <label className={styles.agentsField}>
                <span>Max output tokens</span>
                <input
                  className={styles.metricsInput}
                  value={form.maxOutputTokens}
                  onChange={(e) => setForm((prev) => ({ ...prev, maxOutputTokens: e.target.value }))}
                />
              </label>
            </div>
          ) : null}

          {showMcpNetwork ? (
            <>
              <div className={styles.agentsFormGrid}>
                <label className={styles.agentsField}>
                  <span>Server URL</span>
                  <input
                    className={styles.metricsInput}
                    value={form.url}
                    onChange={(e) => setForm((prev) => ({ ...prev, url: e.target.value }))}
                  />
                </label>
                <label className={styles.agentsField}>
                  <span>Default tool</span>
                  <input
                    className={styles.metricsInput}
                    value={form.defaultTool}
                    onChange={(e) => setForm((prev) => ({ ...prev, defaultTool: e.target.value }))}
                  />
                </label>
              </div>
              <label className={styles.agentsField}>
                <span>Optional HTTP headers</span>
                <span className={styles.agentsInputHint}>JSON object, e.g. custom Authorization or tracing keys.</span>
                <textarea
                  className={styles.agentsTextarea}
                  value={form.headersJson}
                  spellCheck={false}
                  placeholder='{"Authorization": "Bearer …"}'
                  onChange={(e) => setForm((prev) => ({ ...prev, headersJson: e.target.value }))}
                />
              </label>
            </>
          ) : null}

          <AgentDatasetCoverage
            agentId={isCreating ? null : selectedAgent?.id ?? null}
            isCreatingAgent={isCreating}
          />

          {errors.length > 0 ? (
            <div className={`${styles.metricsErrorBox} ${styles.agentsEditorMessage}`}>
              {errors.map((error) => (
                <p key={error}>{error}</p>
              ))}
            </div>
          ) : null}

          {feedback ? (
            <div className={`${styles.metricsSuccessBox} ${styles.agentsEditorMessage}`}>{feedback}</div>
          ) : null}

          <div className={styles.agentsActions}>
            <button
              type="button"
              className={styles.agentsPrimaryBtn}
              onClick={handleSave}
              disabled={busy || !hasFormChanges}
            >
              {busy ? "Working..." : isCreating ? "Create agent" : "Save changes"}
            </button>

            {!isCreating && selectedAgent ? (
              <>
                <button
                  type="button"
                  className={styles.metricsSecondaryBtn}
                  onClick={() => toggleEnabled(selectedAgent.id)}
                  disabled={busy}
                >
                  {selectedAgent.enabled ? "Disable agent" : "Enable agent"}
                </button>
                <button type="button" className={styles.metricsSecondaryBtn} onClick={validateSelected} disabled={busy}>
                  Validate agent
                </button>
                <button
                  type="button"
                  className={styles.metricsDangerBtn}
                  onClick={() => setDeleteModalOpen(true)}
                  disabled={busy}
                >
                  Delete agent
                </button>
              </>
            ) : null}
          </div>
        </section>
      ) : null}

      <ConfirmationModal
        isOpen={deleteModalOpen && Boolean(selectedAgent)}
        onClose={() => {
          if (!busy) setDeleteModalOpen(false);
        }}
        onConfirm={() => void confirmDeleteAgent()}
        title="Delete agent"
        message={
          selectedAgent
            ? `Delete agent "${selectedAgent.name}"? This cannot be undone.`
            : ""
        }
        confirmText="Delete"
        cancelText="Cancel"
        isDestructive
        loading={busy && deleteModalOpen}
      />
    </div>
  );
};

export default AgentsPage;
